import React, { useCallback, useMemo } from 'react';
import { theme } from '../../../../../../tailwind.config';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ITaMetricsByMonth } from 'services/DashboardApi/types';
import { InfoTransparentIcon } from 'ui/Icons/components/InfoTransparent.component';
import { HidingTooltip } from 'ui/Tooltip';
import { useResponsive } from 'containers/CRM/hooks/useResponsive';
import { MetricsTooltip } from '../MetricsTooltip';
import { TA_METRICS_TOOLTIP_ITEMS } from '../tooltipData';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const ACTIVE_TA_COLOR = theme.colors['status-confirmed'];
const LOGGED_IN_TA_COLOR = theme.colors['status-enquiry'];
const NEW_TA_COLOR = theme.colors['status-requested'];

interface ITransformedMonthData extends ITaMetricsByMonth {
  monthLabel: string;
}

const TaMetricsTooltip = ({ active, payload, label }: any) => {
  const getSeriesValue = (dataKey: 'activeTAs' | 'loggedInTAs' | 'newTAs') => {
    const entry = payload?.find((p: any) => p?.dataKey === dataKey);
    const value = entry?.value;
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  };

  if (active && payload && payload.length) {
    const activeTAs = getSeriesValue('activeTAs');
    const loggedInTAs = getSeriesValue('loggedInTAs');
    const newTAs = getSeriesValue('newTAs');

    return (
      <div
        className="bg-white border border-solid border-gray-40 py-[10px] px-[14px] font-pt-sans"
      >
        <p className="font-pt-sans text-[13px] font-bold text-black m-0 mb-[8px]">{label}</p>
        <p className="font-pt-sans text-[12px] m-0 mb-[4px]" style={{ color: ACTIVE_TA_COLOR }}>
          Active TA: <span style={{ color: theme.colors['black'] }}>{Math.round(activeTAs)}</span>
        </p>
        <p className="font-pt-sans text-[12px] m-0 mb-[4px]" style={{ color: LOGGED_IN_TA_COLOR }}>
          Logged In TA: <span style={{ color: theme.colors['black'] }}>{Math.round(loggedInTAs)}</span>
        </p>
        <p className="font-pt-sans text-[12px] m-0" style={{ color: NEW_TA_COLOR }}>
          New TA: <span style={{ color: theme.colors['black'] }}>{Math.round(newTAs)}</span>
        </p>
      </div>
    );
  }
  return null;
};

interface ITaMetricsChartProps {
  taMetricsByMonth: ITaMetricsByMonth[];
}

export const TaMetricsChart: React.FC<ITaMetricsChartProps> = ({ taMetricsByMonth }) => {
  const { isMobile } = useResponsive();
  const chartData = useMemo<ITransformedMonthData[]>(() => {
    return taMetricsByMonth.map(item => ({
      ...item,
      monthLabel: MONTH_LABELS[item.month - 1],
    }));
  }, [taMetricsByMonth]);

  const maxValue = useMemo(() => {
    const allValues = chartData
      .flatMap(d => [d.activeTAs, d.loggedInTAs, d.newTAs])
      .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));

    const max = Math.max(...allValues, 1);
    return Math.ceil(max * 1.1);
  }, [chartData]);

  const renderInfoTooltipContent = useCallback(
    () => <MetricsTooltip items={TA_METRICS_TOOLTIP_ITEMS} />,
    []
  );

  return (
    <div className="bg-white p-[20px] rounded">
      <div className="flex items-center justify-center gap-[5px] mb-[4px]">
        <p className="m-0 font-pt-sans text-[13px] leading-[22px] tracking-[0.52px] uppercase text-black">
          Travel Agent Metrics
        </p>
        <HidingTooltip position="bottom-right" width="220px" renderTooltipContent={renderInfoTooltipContent}>
          <InfoTransparentIcon width={16} height={16} fill={theme.colors['gray-60']} />
        </HidingTooltip>
      </div>

      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          data={chartData}
          margin={{
            top: 20,
            right: 12,
            bottom: 20,
            left: 0,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={theme.colors['gray-17']} vertical={false} />
          <XAxis
            dataKey="monthLabel"
            tick={{ fill: theme.colors['gray-120'], fontSize: 12, fontFamily: 'PT Sans' }}
            axisLine={{ stroke: theme.colors['gray-40'] }}
            tickLine={false}
            padding={{ left: isMobile ? 10 : 40, right: isMobile ? 10 : 40 }}
          />
          <YAxis
            width={36}
            tick={{ fill: theme.colors['gray-120'], fontSize: 12, fontFamily: 'PT Sans' }}
            axisLine={{ stroke: theme.colors['gray-40'] }}
            tickLine={false}
            domain={[0, maxValue]}
            allowDecimals={false}
            tickFormatter={(value: number) => String(Math.round(value))}
          />
          <Tooltip content={<TaMetricsTooltip />} />
          <Line
            type="monotone"
            dataKey="activeTAs"
            stroke={ACTIVE_TA_COLOR}
            strokeWidth={2}
            name="Active TA"
            dot={{ fill: ACTIVE_TA_COLOR, r: 4, strokeWidth: 0 }}
          />
          <Line
            type="monotone"
            dataKey="loggedInTAs"
            stroke={LOGGED_IN_TA_COLOR}
            strokeWidth={2}
            name="Logged In TA"
            dot={{ fill: LOGGED_IN_TA_COLOR, r: 4, strokeWidth: 0 }}
          />
          <Line
            type="monotone"
            dataKey="newTAs"
            stroke={NEW_TA_COLOR}
            strokeWidth={2}
            name="New TA"
            dot={{ fill: NEW_TA_COLOR, r: 4, strokeWidth: 0 }}
          />
        </LineChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div className="flex items-center gap-[20px] mt-[10px] justify-center flex-wrap">
        <div className="flex items-center gap-[5px]">
          <div className="shrink-0 w-[10px] h-[10px]" style={{ backgroundColor: ACTIVE_TA_COLOR }} />
          <p className="font-pt-sans text-[15px] m-0 text-black">Active TA</p>
        </div>
        <div className="flex items-center gap-[5px]">
          <div className="shrink-0 w-[10px] h-[10px]" style={{ backgroundColor: LOGGED_IN_TA_COLOR }} />
          <p className="font-pt-sans text-[15px] m-0 text-black">Logged In TA</p>
        </div>
        <div className="flex items-center gap-[5px]">
          <div className="shrink-0 w-[10px] h-[10px]" style={{ backgroundColor: NEW_TA_COLOR }} />
          <p className="font-pt-sans text-[15px] m-0 text-black">New TA</p>
        </div>
      </div>
    </div>
  );
};
